/**
 * Enhanced Pointer Lock Controls
 * Handles pointer lock with better UX and error handling
 * Follows best practices: encapsulation, cleanup, type safety
 */

import { frontendLogger } from '../../shared/logger';

export interface PointerControlsOptions {
  onLockChange?: (locked: boolean) => void;
  sensitivity?: number;
  showInstructions?: boolean;
  initialYaw?: number;
  initialPitch?: number;
}

export class PointerControls {
  // Private state
  private readonly canvas: HTMLCanvasElement;
  private isLocked = false;
  private readonly onLockChange?: (locked: boolean) => void;
  private instructionsOverlay?: HTMLElement;

  // Mouse movement tracking
  private _yaw = 0;
  private _pitch = 0;
  private _mouseX = 0;
  private _mouseY = 0;
  private _sensitivity = 0.002;

  // Event listener references for cleanup
  private readonly boundHandlers = {
    canvasClick: this.handleCanvasClick.bind(this),
    pointerLockChange: this.handlePointerLockChange.bind(this),
    pointerLockError: this.handlePointerLockError.bind(this),
    mouseMove: this.handleMouseMove.bind(this),
    keyDown: this.handleKeyDown.bind(this)
  };

  constructor(canvas: HTMLCanvasElement, options: PointerControlsOptions = {}) {
    this.canvas = canvas;
    this.onLockChange = options.onLockChange;
    this._sensitivity = options.sensitivity ?? 0.002;
    this._yaw = options.initialYaw ?? 0;
    this._pitch = options.initialPitch ?? 0;

    this.setupPointerLock();
    if (options.showInstructions !== false) {
      this.setupInstructions();
    }
    this.setupEventListeners();

    frontendLogger.info('PointerControls initialized', {
      sensitivity: this._sensitivity,
      showInstructions: options.showInstructions !== false,
      initialYaw: this._yaw,
      initialPitch: this._pitch
    });
  }

  // Public getters for read-only access
  public get yaw(): number { return this._yaw; }
  public get pitch(): number { return this._pitch; }
  public get mouseX(): number { return this._mouseX; }
  public get mouseY(): number { return this._mouseY; }
  public get sensitivity(): number { return this._sensitivity; }

  private setupPointerLock(): void {
    this.canvas.addEventListener('click', this.boundHandlers.canvasClick);
    document.addEventListener('pointerlockchange', this.boundHandlers.pointerLockChange);
    document.addEventListener('pointerlockerror', this.boundHandlers.pointerLockError);
  }

  private setupInstructions(): void {
    let instructions = document.getElementById('instructions');

    if (!instructions) {
      instructions = document.createElement('div');
      instructions.id = 'instructions';
      document.body.appendChild(instructions);
    }

    instructions.innerHTML = this.getInstructionsHTML();
    this.instructionsOverlay = instructions;
    this.updateInstructions();
  }

  private getInstructionsHTML(): string {
    return `
      <div style="
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 40px;
        border-radius: 12px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
        text-align: center;
        z-index: 1000;
        max-width: 450px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.8);
        border: 2px solid #ff6b35;
      ">
        <h2 style="margin: 0 0 20px 0; color: #ff6b35; font-size: 28px;">🎮 Borderboxes</h2>
        <p style="font-size: 16px; margin-bottom: 25px;"><strong>Click anywhere to start</strong></p>

        <div style="text-align: left; margin: 25px 0; font-size: 14px; line-height: 1.8;">
          <p style="margin: 0 0 15px 0; color: #ff6b35; font-weight: bold;">Controls:</p>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
            <span>• <strong>WASD</strong> - Move</span>
            <span>• <strong>Shift</strong> - Sprint</span>
            <span>• <strong>Mouse</strong> - Look</span>
            <span>• <strong>C</strong> - Crouch</span>
            <span>• <strong>Left Click</strong> - Shoot</span>
            <span>• <strong>Ctrl</strong> - Slide</span>
            <span>• <strong>Right Click</strong> - ADS</span>
            <span>• <strong>E</strong> - Pickup</span>
            <span>• <strong>1-5</strong> - Weapons</span>
            <span>• <strong>ESC</strong> - Pause</span>
          </div>
        </div>

        <div style="margin-top: 25px; padding-top: 20px; border-top: 1px solid rgba(255, 255, 255, 0.2);">
          <p style="font-size: 13px; color: #aaa; margin: 0;">Press <strong>ESC</strong> anytime to release cursor</p>
        </div>
      </div>
    `;
  }

  private setupEventListeners(): void {
    document.addEventListener('mousemove', this.boundHandlers.mouseMove);
    document.addEventListener('keydown', this.boundHandlers.keyDown);
  }

  private handleCanvasClick(): void {
    if (!this.isLocked) {
      this.requestLock();
    }
  }

  private handlePointerLockChange(): void {
    const wasLocked = this.isLocked;
    this.isLocked = document.pointerLockElement === this.canvas;

    if (wasLocked !== this.isLocked) {
      frontendLogger.info(`Pointer lock ${this.isLocked ? 'acquired' : 'released'}`);
      this.updateInstructions();
      this.onLockChange?.(this.isLocked);
    }
  }

  private handlePointerLockError(): void {
    frontendLogger.error('Pointer lock failed', new Error('Pointer lock request failed'));
    alert('Pointer lock failed. Please try clicking the canvas again.');
  }

  private handleMouseMove(e: MouseEvent): void {
    if (this.isLocked) {
      // FPS-style mouse look with pointer lock
      this._yaw -= e.movementX * this._sensitivity;
      this._pitch -= e.movementY * this._sensitivity;
      // Clamp pitch to prevent camera flipping
      this._pitch = Math.max(-Math.PI / 2.5, Math.min(Math.PI / 2.5, this._pitch));
    } else {
      // Fallback for non-pointer-lock (for debugging/testing)
      this._mouseX = (e.clientX / window.innerWidth) * 2 - 1;
      this._mouseY = -(e.clientY / window.innerHeight) * 2 + 1;
    }
  }

  private handleKeyDown(_e: KeyboardEvent): void {
    // ESC key handling moved to PauseMenu
    // This method is kept for potential future keyboard shortcuts
  }

  private updateInstructions(): void {
    if (this.instructionsOverlay) {
      this.instructionsOverlay.style.display = this.isLocked ? 'none' : 'block';
    }
  }

  public requestLock(): void {
    try {
      this.canvas.requestPointerLock();
      frontendLogger.debug('Pointer lock requested');
    } catch (error) {
      frontendLogger.error('Failed to request pointer lock', error);
    }
  }

  public releaseLock(): void {
    try {
      if (document.pointerLockElement) {
        document.exitPointerLock();
        frontendLogger.debug('Pointer lock released');
      }
    } catch (error) {
      frontendLogger.error('Failed to release pointer lock', error);
    }
  }

  public isPointerLocked(): boolean {
    return this.isLocked;
  }

  public getRotation(): { yaw: number; pitch: number } {
    return { yaw: this._yaw, pitch: this._pitch };
  }

  public getMousePosition(): { x: number; y: number } {
    return { x: this._mouseX, y: this._mouseY };
  }

  public setSensitivity(sensitivity: number): void {
    if (sensitivity < 0.0001 || sensitivity > 0.1) {
      frontendLogger.warn('Sensitivity out of recommended range', { sensitivity });
    }
    this._sensitivity = sensitivity;
    frontendLogger.info('Mouse sensitivity updated', { sensitivity });
  }

  public reset(): void {
    this._yaw = 0;
    this._pitch = 0;
    this._mouseX = 0;
    this._mouseY = 0;
    frontendLogger.debug('Pointer controls reset');
  }

  /**
   * Cleanup method to remove event listeners and prevent memory leaks
   * Call this when destroying the controls or changing scenes
   */
  public destroy(): void {
    // Remove event listeners
    this.canvas.removeEventListener('click', this.boundHandlers.canvasClick);
    document.removeEventListener('pointerlockchange', this.boundHandlers.pointerLockChange);
    document.removeEventListener('pointerlockerror', this.boundHandlers.pointerLockError);
    document.removeEventListener('mousemove', this.boundHandlers.mouseMove);
    document.removeEventListener('keydown', this.boundHandlers.keyDown);

    // Release pointer lock if active
    if (this.isLocked) {
      this.releaseLock();
    }

    // Remove instructions overlay
    if (this.instructionsOverlay && this.instructionsOverlay.parentNode) {
      this.instructionsOverlay.parentNode.removeChild(this.instructionsOverlay);
    }

    frontendLogger.info('PointerControls destroyed');
  }
}
